/**
 * useHelpdeskSocket — subscribe to helpdesk:* Socket.IO events
 *
 * Reuses existing socket singleton (src/socket/index.js). Injects JWT auth
 * + reconnect on first call. Provides imperative callback registration so
 * callers (Inbox, TicketView) react to backend events without polling.
 *
 * Backend events:
 *   helpdesk:new_message      — new inbound message persisted
 *   helpdesk:ticket_assigned  — ticket assignee changed
 *   helpdesk:ai_draft_ready   — AI generated a draft
 *   helpdesk:sla_breached     — SLA cron flagged breach
 *   helpdesk:status_changed   — ticket status transition
 *
 * Part of: Helpdesk Module (Phase 06)
 */

import { useEffect, useRef } from 'react'
import { socket, ensureSocketAuthed, resetSocketAuth } from 'socket'

const HELPDESK_EVENTS = [
  'helpdesk:new_message',
  'helpdesk:ticket_assigned',
  'helpdesk:ai_draft_ready',
  'helpdesk:sla_breached',
  'helpdesk:status_changed',
]

// Auth socket dùng chung ở socket/index.js (chuông thông báo cũng cần) — giữ tên cũ cho caller.
const ensureAuthed = ensureSocketAuthed
export const resetHelpdeskAuth = resetSocketAuth

/**
 * Subscribe to helpdesk events. Pass `handlers` map: { 'event': callback }.
 * Cleans up listeners on unmount.
 *
 * @example
 *   useHelpdeskSocket({
 *     'helpdesk:new_message': (data) => refresh(),
 *     'helpdesk:sla_breached': (data) => notification.warning({ ... }),
 *   })
 */
export function useHelpdeskSocket(handlers = {}) {
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers

  useEffect(() => {
    ensureAuthed()

    // Wrap each handler so we can detach cleanly + always use latest closure
    const wrappers = {}
    for (const event of HELPDESK_EVENTS) {
      wrappers[event] = (data) => {
        const fn = handlersRef.current[event]
        if (typeof fn === 'function') {
          try {
            fn(data)
          } catch (err) {
            console.error('[useHelpdeskSocket]', event, 'handler error:', err)
          }
        }
      }
      socket.on(event, wrappers[event])
    }

    return () => {
      for (const event of HELPDESK_EVENTS) {
        socket.off(event, wrappers[event])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
